import { AsyncSignalValue } from '@component/async.component';
import { FormWorker } from '@feature/dynamic_form/form_manager/form_worker';
import { signal } from '@preact/signals-react';
import { Injectable, Scopes, ControllerProtocol } from '@visiba/instantiation';
import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { BookingScreen, ViewBookingAnalyticActions, ViewBookingController } from '../view_booking.controller';
import { type BookingBasket, BookingBasketService } from '../booking_basket.service';
import type { FormApiModel } from '@api_patient/generated/clientv2/models';

@Injectable({
	scope: Scopes.Scoped,
})
export class ScreenFormsController implements ControllerProtocol {
	public forms = signal<AsyncSignalValue<FormApiModel[]>>({
		flowState: 'pending',
		value: null,
	});
	public formWorker = signal<AsyncSignalValue<Readonly<FormWorker>>>({
		flowState: 'pending',
		value: null,
	});
	private tempDisposers: (() => void)[] = [];
	private basket: BookingBasket | null = null;

	constructor(
		private readonly viewBookingController: ViewBookingController,
		private readonly bookingBasketService: BookingBasketService,
		private readonly analyticService: AnalyticService,
	) {
		this.basket = this.bookingBasketService.get();
	}

	async whenMount() {
		this.analyticService.trackPageView({
			customTitle: 'Booking: Dynamic forms',
		});

		if (!this.allowedToBeHere()) {
			this.analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.NotAllowedToBeHere, 'Dynamic forms');

			this.viewBookingController.goToScreen(BookingScreen.Location);

			return;
		}

		const restoreResult = await this.bookingBasketService.restore();
		if (restoreResult.err) {
			this.analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.ErrorFetchingDynamicForms);
			this.viewBookingController.goToScreen(BookingScreen.Error);

			return;
		}

		if (this.bookingBasketService.forms.length < 1) {
			this.analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.NoDynamicForms);
			this.viewBookingController.goToScreen(BookingScreen.Conclusion, { replace: true });

			return;
		}

		this.forms.value = {
			flowState: 'accessible',
			value: this.bookingBasketService.forms,
		};

		this.analyticService.trackEvent(
			AnalyticCategories.Booking,
			ViewBookingAnalyticActions.NumberOfDynamicForms,
			` ${this.bookingBasketService.forms.length}`,
		);
	}

	whenDemount() {
		this.tempDisposers.forEach((x) => x());
	}

	public async loadSingleForm(formId: number): Promise<void> {
		if (this.basket == null) {
			this.formWorker.value = {
				flowState: 'error',
				value: null,
			};

			return;
		}

		this.formWorker.value = {
			flowState: 'pending',
			value: null,
		};

		const formWorker = this.bookingBasketService.formManager.getFormByFormId(formId);
		if (formWorker == null) {
			this.formWorker.value = {
				flowState: 'error',
				value: null,
			};

			return;
		}

		this.tempDisposers.forEach((x) => x());
		this.tempDisposers = [
			formWorker.events.on('onCommit', () => {
				const nextFormId = this.getNextFormId(formWorker.formId);
				if (nextFormId != null) {
					this.viewBookingController.goToScreen(BookingScreen.Forms, { formId: nextFormId });
				} else {
					this.viewBookingController.goToScreen(BookingScreen.Conclusion);
				}
			}),
			formWorker.events.on('onChangePage', (data) => {
				this.viewBookingController.goToScreen(BookingScreen.Forms, { formId: formWorker.formId, formPage: data.newPagePosition });
			}),
		];

		this.formWorker.value = {
			flowState: 'accessible',
			value: formWorker,
		};
	}

	public getNextFormId(currentFormId: number): number | null {
		if (!this.bookingBasketService.forms) {
			throw new Error('Forms not loaded...');
		}

		const currentFormIndex = this.bookingBasketService.forms.findIndex((form) => form.Id === currentFormId);

		return this.bookingBasketService.forms[currentFormIndex + 1]?.Id ?? null;
	}

	private allowedToBeHere() {
		// If we don't have any user data, we can't be here.
		if (this.basket == null) {
			return false;
		}

		// If we don't have flow data, we can't be here.
		const flowData = this.viewBookingController.flowData.value.value;
		if (!flowData) {
			return false;
		}

		// If we don't have a selected respondent type, we can't be here.
		if (this.bookingBasketService.get().respondantType == null) {
			return false;
		}

		// If we have reason for visits, we need to have a selected reason for visit.
		if (
			flowData.useReasonForVisits &&
			this.viewBookingController.getFilteredReasonForVisits().length > 0 &&
			!this.viewBookingController.getSelectedReasonForVisit()
		) {
			return false;
		}

		if (!this.basket.timeSlotId) {
			return false;
		}

		return true;
	}
}
